// src/components/Modals/AddCollectorModal.jsx
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import './ModalBase.css';
import { FaTimes, FaUndo } from 'react-icons/fa';
import {
  Map,
  Marker,
  Source,
  Layer,
  NavigationControl,
  FullscreenControl
} from 'react-map-gl';
import Select from 'react-select';
import 'mapbox-gl/dist/mapbox-gl.css';
import API_BASE_URL from '../../config';

/* ──────────────── константы ──────────────── */
const MAPBOX_TOKEN =
  'pk.eyJ1IjoiaWdvcnNlcmdlZXZpY2hpc2l0IiwiYSI6ImNtNm5oMXd0aTA2bnQycXNnODlycjJtcGEifQ.NSbRJio0u0pnKfQkBluU_Q'; // ← замените своим
const MODAL_WIDTH = 620; // ещё чуть уже
const MAP_HEIGHT  = 240;

const toFixed = (num) => +(+num).toFixed(6);

/* ──────────────── стили для Select ──────────────── */
const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base, state) => ({
    ...base,
    backgroundColor: '#202127',
    borderColor: state.isFocused ? '#FF740F' : '#3B3B40',
    '&:hover': { borderColor: '#FF740F' },
    borderRadius: 8,
    minHeight: 44,
    height: 44,
    boxShadow: 'none',
    padding: '0 8px',
  }),
  valueContainer: (base) => ({ ...base, height: 36, padding: '0 8px' }),
  indicatorsContainer: (base) => ({ ...base, height: 36 }),
  indicatorSeparator: () => ({ display: 'none' }),
  singleValue: (base) => ({ ...base, color: '#e5e7eb' }),
  placeholder: (base) => ({ ...base, color: '#8b919d' }),
  menu: (base) => ({ ...base, backgroundColor: '#2a2c33' }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#3B3B40' : '#2a2c33',
    color: '#e5e7eb',
  }),
};

export default function AddCollectorModal({ onClose, onAdded }) {
  /* ──────────────── поля ──────────────── */
  const [name,  setName]  = useState('');
  const [year,  setYear]  = useState('');
  const [material, setMaterial]         = useState('');
  const [length,   setLength]           = useState('');
  const [diameter, setDiameter]         = useState('');
  const [nodeFlow, setNodeFlow]         = useState('');
  const [expenditure, setExpenditure]   = useState('');
  const [techCondition, setTechCondition] = useState('');
  const [designId, setDesignId]         = useState(null);
  const [designOptions, setDesignOptions] = useState([]);

  /* ──────────────── загрузка конструктивных решений ──────────────── */
  useEffect(() => {
    fetch(`${API_BASE_URL}/collector/get_all_design`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        const opts = (data || []).map((d) => ({ value: d.id, label: d.collector_design }));
        setDesignOptions(opts);
      })
      .catch(() => setDesignOptions([]));
  }, []);

  /* ──────────────── геометрия ──────────────── */
  const [points, setPoints] = useState([]);
  const [viewState, setViewState] = useState(() => {
    const saved = localStorage.getItem('addCollectorViewport');
    return saved
      ? JSON.parse(saved)
      : { longitude: 88.193, latitude: 69.3515, zoom: 13.4, pitch: 0, bearing: 57 };
  });

  const handleMapClick = useCallback((e) => {
    const { lng, lat } = e.lngLat;
    setPoints((p) => [...p, { latitude: lat, longitude: lng }]);
  }, []);

  const undoPoint = () => setPoints((p) => p.slice(0, -1));

  const lineGeoJSON = useMemo(
    () =>
      points.length < 2
        ? null
        : {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: points.map((p) => [p.longitude, p.latitude]),
            },
          },
    [points]
  );

  /* ──────────────── сохранение ──────────────── */
  const handleSave = () => {
    if (points.length < 2) return alert('Добавьте как минимум две точки.');
    if (!name.trim())   return alert('Введите название коллектора.');

    const latitudes  = points.map((p) => toFixed(p.latitude)).join(';');
    const longitudes = points.map((p) => toFixed(p.longitude)).join(';');

    const payload = {
      name: name.trim(),
      collector_design_id: designId ?? 0,
      year,
      material,
      length,
      diameter,
      node_flow: nodeFlow,
      expenditure,
      technical_condition: techCondition,
      latitude:  latitudes,
      longitude: longitudes,
    };

    fetch(`${API_BASE_URL}/collector/create_collector`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((created) => {
        onAdded?.(created.id ?? created);
        onClose();
      })
      .catch(() => alert('Не удалось добавить коллектор.'));
  };

  /* ──────────────── локальные стили ──────────────── */
  const gridStyle  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', width: '100%' };
  const inputStyle = { width: '100%' };

  /* ──────────────── render ──────────────── */
  return (
    <div className="modal-overlay">
      <div
        className="modal-window add-house-modal"
        style={{ width: MODAL_WIDTH, maxWidth: '90vw', maxHeight: '95vh', overflowY: 'auto' }}
      >
        <button className="modal-close-btn" onClick={onClose}>
          <FaTimes />
        </button>
        <h2 className="modal-title">Добавить коллектор</h2>

        <div className="modal-form">
          {/* карта */}
          <label>Положение коллектора на карте (кликайте последовательно):</label>
          <div className="modal-map-container" style={{ position: 'relative', height: MAP_HEIGHT }}>
            <Map
              {...viewState}
              onMove={(e) => {
                setViewState(e.viewState);
                localStorage.setItem('addCollectorViewport', JSON.stringify(e.viewState));
              }}
              mapboxAccessToken={MAPBOX_TOKEN}
              style={{ width: '100%', height: '100%' }}
              mapStyle="mapbox://styles/mapbox/dark-v11"
              onClick={handleMapClick}
              dragRotate
              touchRotate
            >
              <FullscreenControl position="top-left" />
              <NavigationControl position="top-left" />

              {points.map((p, idx) => (
                <Marker
                  key={idx}
                  latitude={p.latitude}
                  longitude={p.longitude}
                  anchor="bottom"
                >
                  <div className="house-marker" />
                </Marker>
              ))}
              {lineGeoJSON && (
                <Source id="collector-line" type="geojson" data={lineGeoJSON}>
                  <Layer
                    id="collector-line-layer"
                    type="line"
                    paint={{ 'line-width': 4, 'line-color': '#eb5717' }}
                  />
                </Source>
              )}
            </Map>
          </div>

          {/* undo + счётчик */}
          <div
            style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, margin: '2px 0' }}
          >
            <span style={{ opacity: 0.8, fontSize: 16 }}>
              Точек: <strong>{points.length}</strong>
            </span>
            <button
              type="button"
              className="primary-btn"
              onClick={undoPoint}
              disabled={!points.length}
              style={{ padding: '2px 6px', fontSize: 14 }}
            >
              <FaUndo style={{ marginRight: 4 }} />Отменить точку
            </button>
          </div>

          {/* форма */}
          <div style={gridStyle}>
            <div>
              <label>Название коллектора:</label>
              <input
                style={inputStyle}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Введите название коллектора"
              />
            </div>
            <div>
              <label>Год ввода:</label>
              <input
                style={inputStyle}
                value={year}
                onChange={(e) => setYear(e.target.value.replace(/\D+/g, ''))}
                placeholder="Введите год ввода"
              />
            </div>
            <div>
              <label>Конструктивное решение:</label>
              <Select
                classNamePrefix="modal-select"
                styles={selectStyles}
                options={designOptions}
                value={designOptions.find((o) => o.value === designId) || null}
                onChange={(opt) => setDesignId(opt?.value ?? null)}
                placeholder="Выберите решение"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="absolute"
              />
            </div>
            <div>
              <label>Материал:</label>
              <input
                style={inputStyle}
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
                placeholder="Введите материал"
              />
            </div>
            <div>
              <label>Длина (м.п.):</label>
              <input
                style={inputStyle}
                value={length}
                onChange={(e) => setLength(e.target.value.replace(/\D+/g, ''))}
                placeholder="Введите длину"
              />
            </div>
            <div>
              <label>Расход на участке (л/с):</label>
              <input
                style={inputStyle}
                value={expenditure}
                onChange={(e) => setExpenditure(e.target.value.replace(/[^0-9.]/g, ''))}
                placeholder="Введите расход"
              />
            </div>
            <div>
              <label>Диаметр:</label>
              <input
                style={inputStyle}
                value={diameter}
                onChange={(e) => setDiameter(e.target.value)}
                placeholder="Введите диаметр"
              />
            </div>
            <div>
              <label>Узловой расход (л/с):</label>
              <input
                style={inputStyle}
                value={nodeFlow}
                onChange={(e) => setNodeFlow(e.target.value.replace(/[^0-9.]/g, ''))}
                placeholder="Введите узловой расход"
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Техническое состояние:</label>
              <textarea
                style={{ ...inputStyle, minHeight: 32 }}
                rows={2}
                value={techCondition}
                onChange={(e) => setTechCondition(e.target.value)}
                placeholder="Введите техническое состояние"
              />
            </div>
          </div>
        </div>

        <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button className="modal-button cancel" onClick={onClose}>
            Отмена
          </button>
          <button className="modal-button save" onClick={handleSave}>
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}
